// The Terminal console's backfill data layer, and the render-in-the-browser
// half of D-L (`docs handoff overview.md`): the wire carries `{ key, params }`,
// never a sentence, because `@developerz/domain` cannot reach the i18n
// catalogue (verified there, in `wire/terminal.ts`'s own header). This file is
// where that key becomes English (or Spanish) prose.
//
// `GET /v1/terminal/events` — keyset-paged, newest-first, mirrors
// `lib/audit-feed.ts`. Backfill requests the route's own page-size ceiling
// (`TERMINAL_EVENTS_MAX_PAGE_SIZE`) on every page, including a scroll-back
// page: the ceiling is a per-request size, not a one-time budget. D-O's
// "newest 500 lines" is the sum across pages, not one request — see
// `TERMINAL_BACKFILL_PAGE_SIZE`'s own docstring for why 500 was never a
// per-request number the route could actually serve.

import type { TerminalActor, TerminalEvent, TerminalLevel, TerminalLine } from '@developerz/domain';
import { TERMINAL_EVENTS_MAX_PAGE_SIZE, TerminalEventSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import type { Page } from './paged';
import { pageSchema } from './paged-schema';

/** One backfilled line — the published `TerminalEvent`, unmirrored. */
export type TerminalEventRow = TerminalEvent;

const terminalEventsPageSchema = pageSchema(TerminalEventSchema);

export const TERMINAL_EVENTS_QUERY_KEY = 'terminal_events';

/**
 * D-O said "backfill is the newest 500 lines"; the route only ever allowed
 * 200 (`TERMINAL_EVENTS_MAX_PAGE_SIZE`, `@developerz/domain`) — every
 * backfill request 400'd until this read the same constant the route
 * enforces, rather than a second, independently-drifted copy of "500"
 * (#4395 review). "Newest 500" now means the newest 500 across pages: this
 * page size times `usePagedQuery`'s own scroll-back ("load earlier") pulls
 * the rest, same ring buffer, same request shape, no separate code path.
 */
export const TERMINAL_BACKFILL_PAGE_SIZE = TERMINAL_EVENTS_MAX_PAGE_SIZE;

/**
 * One page of the account's Terminal backfill, newest-first. Idle with no
 * session. `cursor()` pages BACKWARD in time — the same keyset shape
 * `lib/audit-feed.ts` and `lib/digests.ts` already page, via `usePagedQuery`.
 *
 * `verbose()` is the live tail's own `?verbose=` (`use-terminal-panes.ts`
 * `anyVerbose`, `terminal-stream.ts`): on, the backfill widens to the
 * `task_run_events` rows too. Without it a verbose pane after a reload showed
 * run events from connect time onward and none before (#5608 slice 17 row 20).
 * It is in the key, so a flip re-reads; a cursor held from the other listing is
 * the caller's to drop (`list.reset()`).
 */
export function useTerminalEvents(
  cursor: () => string | null,
  token: string | null,
  verbose: () => boolean,
) {
  return createQuery<Page<TerminalEventRow>>(() => {
    // Absent is audit-only: `?verbose=false` is not sent, the stream's spelling.
    const base = `/v1/terminal/events?per_page=${TERMINAL_BACKFILL_PAGE_SIZE}${verbose() ? '&verbose=true' : ''}`;
    const path = withCursor(base, cursor());
    return {
      queryKey: [TERMINAL_EVENTS_QUERY_KEY, path, token, activeAccountId()],
      enabled: token !== null,
      queryFn: () => apiFetch(path, token, undefined, { schema: terminalEventsPageSchema }),
    };
  });
}

/**
 * Every {@link TerminalActor}'s display word — a `Record` over the CLOSED
 * union, so a member `@developerz/domain` adds is a compile error here before
 * it ships as a blank "actor@subject" column. Static `t()` calls, one per
 * member: `i18n.coverage.test.ts` sees every one of them.
 */
const ACTOR_LABEL: Readonly<Record<TerminalActor, () => string>> = {
  coder: () => t('terminal.actor_coder'),
  reviewer: () => t('terminal.actor_reviewer'),
  babysitter: () => t('terminal.actor_babysitter'),
  helper: () => t('terminal.actor_helper'),
  // Computed, unlike every sibling key: an unquoted `system:` here is a UI
  // label for the TerminalActor union member, never a string reaching a
  // model, but scripts/system-prompt-seams.test.ts's field scan cannot tell
  // the two apart from the bare token alone, and that roster is reserved for
  // real prompt composers — adding an unrelated i18n lookup to it would be a
  // false entry in a security-audited list, worse than dodging the pattern.
  ['system']: () => t('terminal.actor_system'),
  human: () => t('terminal.actor_human'),
  mcp: () => t('terminal.actor_mcp'),
};

export function actorLabel(actor: TerminalActor): string {
  return ACTOR_LABEL[actor]();
}

/** Every {@link TerminalLevel}'s display word — the same closed-union table. */
const LEVEL_LABEL: Readonly<Record<TerminalLevel, () => string>> = {
  info: () => t('terminal.level_info'),
  ok: () => t('terminal.level_ok'),
  warn: () => t('terminal.level_warn'),
  fail: () => t('terminal.level_fail'),
  decision: () => t('terminal.level_decision'),
};

export function levelLabel(level: TerminalLevel): string {
  return LEVEL_LABEL[level]();
}

/**
 * Render one line's sentence.
 *
 * `line.key` is `terminal.${string}` — a template-literal type, not a closed
 * union, because the five renderer families (`@developerz/domain`) own their
 * own keys and a closed union here would be edited by five agents at once
 * (the same reasoning `wire/terminal.ts` gives for not closing it there). TS
 * therefore cannot enumerate every real key the way it enumerates
 * {@link TerminalActor} above, so this is a plain dynamic `t()` call rather
 * than a hand-written `Record<TerminalLineKey, …>` — and the coverage that
 * table would have bought is `terminal-events.test.ts` instead: it renders
 * every kind the renderer table can produce and asserts `en.json` has every
 * key that comes out, so a family that adds a kind with no catalog copy fails
 * there rather than shipping `⟦terminal.…⟧` to a customer.
 */
export function terminalLineText(line: TerminalLine): string {
  return t(line.key, line.params);
}
